import { GiftPreference, PurchaseItemObject, UserDetailsRequirement } from "types";

// API defaults, so saving before a seller has gift preferences changes nothing.
export const DEFAULT_GIFT_PREFERENCE: GiftPreference = {
    enabled: true,
    showAdditionalExperiences: false,
    customerPhoneRequired: true,
    captchaEnabled: true,
    allowMultiItemGifts: true,
    showDemographics: true,
    recipientDetailsRequirement: UserDetailsRequirement.REQUIRED,
    organizerDetailsRequirement: UserDetailsRequirement.REQUIRED,
};

export const getGiftDetailsRequirements = (preference?: GiftPreference) => ({
    recipientDetailsRequirement: preference?.recipientDetailsRequirement ?? UserDetailsRequirement.REQUIRED,
    organizerDetailsRequirement: preference?.organizerDetailsRequirement ?? UserDetailsRequirement.REQUIRED,
});

const REQUIREMENT_STRICTNESS = [
    UserDetailsRequirement.NOT_COLLECTED,
    UserDetailsRequirement.OPTIONAL_NO_CONFIRMATION,
    UserDetailsRequirement.OPTIONAL_CONFIRMATION,
    UserDetailsRequirement.REQUIRED,
];

export const getStrictestRequirement = (requirements: UserDetailsRequirement[]) =>
    REQUIREMENT_STRICTNESS[
        Math.max(...requirements.map((requirement) => REQUIREMENT_STRICTNESS.indexOf(requirement)))
    ] ?? UserDetailsRequirement.REQUIRED;

interface RecipientState {
    isScannedGiftCard?: boolean;
    isRecipientSkipped?: boolean;
}

export const getRecipientRules = (requirement: UserDetailsRequirement, item: RecipientState) => {
    // Scanned physical cards never collect recipient details, whatever the setting.
    const isRecipientShown = !item.isScannedGiftCard && requirement !== UserDetailsRequirement.NOT_COLLECTED;
    // Only "with confirmation" asks the seller to confirm a skip; "no confirmation" just leaves the fields optional.
    const isSkippable = isRecipientShown && requirement === UserDetailsRequirement.OPTIONAL_CONFIRMATION;
    const isSkipped = isSkippable && (item.isRecipientSkipped ?? false);
    const isRecipientCollected = isRecipientShown && !isSkipped;
    const isRecipientRequired =
        isRecipientCollected &&
        (requirement === UserDetailsRequirement.REQUIRED ||
            requirement === UserDetailsRequirement.OPTIONAL_CONFIRMATION);

    return { isRecipientShown, isSkippable, isSkipped, isRecipientCollected, isRecipientRequired };
};

interface GiftItemState extends RecipientState {
    object: PurchaseItemObject;
}

// "Same as recipient" is ambiguous when several gifts collect a recipient, so it needs exactly one. A lone
// skipped gift is still returned so the toggle shows, disabled.
export const getOnlyGiftWithRecipient = <T extends GiftItemState>(items: T[], requirement: UserDetailsRequirement) => {
    const shownGifts = items.filter(
        (item) => item.object === PurchaseItemObject.GIFT && getRecipientRules(requirement, item).isRecipientShown,
    );
    const collectedGifts = shownGifts.filter((item) => getRecipientRules(requirement, item).isRecipientCollected);

    if (collectedGifts.length === 1) {
        return collectedGifts[0];
    }

    return collectedGifts.length === 0 && shownGifts.length === 1 ? shownGifts[0] : undefined;
};

// In gift-only carts the organizer setting replaces the seller's email rule, except Required, which keeps it.
export const isOrganizerEmailRequirementEnabled = (
    giftOrganizerRequirement: UserDetailsRequirement | null,
    isSellerEmailRequired: boolean,
) => {
    if (giftOrganizerRequirement === null || giftOrganizerRequirement === UserDetailsRequirement.REQUIRED) {
        return isSellerEmailRequired;
    }

    return giftOrganizerRequirement === UserDetailsRequirement.OPTIONAL_CONFIRMATION;
};
